import React, { useReducer, useState } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import { CHANNEL_ICON, CHANNEL_LABEL } from '../../recall-builder/types';
import type { RcListRow } from '../../recall-campaigns/types';
import {
  LIST_GOAL_LABEL,
  deleteRecall,
  duplicateRecall,
  listHeader,
  resultNoun,
  sortedRows,
  toggleRecall,
} from '../../recall-campaigns/store';

export interface RcListViewProps {
  onNew?: () => void;
  onOpenSettings?: () => void;
  onReport?: (id: string) => void;
  onEdit?: (id: string) => void;
  onContinue?: (id: string) => void;
  onDuplicated?: (id: string) => void;
  /** Only when the Recall agent is on hold. */
  onHold?: boolean;
  onResume?: () => void;
}

const COLS = 'grid-cols-[minmax(0,1.9fr)_140px_110px_minmax(0,1fr)_130px_minmax(0,1fr)]';

/**
 * The recall list (amendment 22). **Three states, four actions, one number per
 * row.**
 *
 * Tested on staging, the old page looked finished and mostly did nothing: the
 * toggle, Duplicate and row clicks were inert, every Report was the Hygiene
 * report, Setup opened the old sequence page for every recall, and Settings
 * opened General settings. Every control here goes somewhere.
 */
export function RcListView({
  onNew,
  onOpenSettings,
  onReport,
  onEdit,
  onContinue,
  onDuplicated,
  onHold = false,
  onResume,
}: RcListViewProps) {
  // The store is module-level so four surfaces share it; this forces a repaint
  // when the toggle, Duplicate or Delete write to it.
  const [, force] = useReducer((n: number) => n + 1, 0);
  const [toast, setToast] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<RcListRow | null>(null);

  const rows = sortedRows();
  const head = listHeader();

  const say = (message: string) => {
    setToast(message);
    window.setTimeout(() => { setToast(null); }, 2600);
  };

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1300px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          {/* Computed, all four of them. No money in the header. */}
          <div className="pc-eyebrow text-[var(--text-secondary)]">
            {head.recalls} recalls · {head.active} active · {head.inProgress.toLocaleString('en-US')} patients in
            progress · {head.booked} booked in the last 30 days
          </div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
            Recall campaigns
          </h1>
          <p className="m-0 mt-0.5 max-w-[680px] text-[12.5px] text-[var(--text-secondary)]">
            Messages that go out when patients are due — for a cleaning, an exam, or a visit they’ve missed.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Went to /settings/general before. */}
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>
            Settings
          </Button>
          <Button variant="primary" size="sm" iconLeft="plus" onClick={onNew}>
            New recall
          </Button>
        </div>
      </div>

      {onHold ? (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-[10px] bg-[#FBF3E4] px-4 py-2.5">
          <span className="text-[12.5px] font-semibold text-[var(--warn-amber)]">
            Recall is on hold — nothing is sending.
          </span>
          <button
            type="button"
            onClick={onResume}
            className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
          >
            Resume
          </button>
        </div>
      ) : null}

      <div className="mt-4 overflow-hidden rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
        <div
          className={cn(
            'grid items-center gap-x-3 border-b border-[var(--border-default)] bg-[var(--surface-header)] px-4 py-2.5 font-[var(--font-mono)] text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]',
            COLS,
          )}
        >
          <span>Recall</span>
          <span>State</span>
          <span>In progress</span>
          <span>Messages</span>
          <span>Last 30 days</span>
          <span className="text-right">Actions</span>
        </div>

        {rows.length === 0 ? (
          <EmptyState onNew={onNew} />
        ) : (
          rows.map((r, i) => (
            <Row
              key={r.id}
              row={r}
              last={i === rows.length - 1}
              onOpen={() => {
                // Row click: a running recall opens its report, a draft resumes.
                if (r.state === 'draft') onContinue?.(r.id);
                else onReport?.(r.id);
              }}
              onToggle={() => {
                const next = toggleRecall(r.id);
                force();
                if (next) say(next.state === 'paused' ? `${next.name} paused` : `${next.name} is running again`);
              }}
              onReport={() => { onReport?.(r.id); }}
              onEdit={() => { onEdit?.(r.id); }}
              onContinue={() => { onContinue?.(r.id); }}
              onDuplicate={() => {
                const copy = duplicateRecall(r.id);
                force();
                if (copy) {
                  say(`${copy.name} created`);
                  onDuplicated?.(copy.id);
                }
              }}
              onDelete={() => { setConfirming(r); }}
            />
          ))
        )}
      </div>

      {/* In the page, never `window.confirm` (§5). */}
      {confirming !== null ? (
        <DeleteModal
          row={confirming}
          onCancel={() => { setConfirming(null); }}
          onConfirm={() => {
            const name = confirming.name;
            deleteRecall(confirming.id);
            setConfirming(null);
            force();
            say(`${name} deleted`);
          }}
        />
      ) : null}

      {toast !== null ? (
        <div className="fixed bottom-6 left-1/2 z-[80] -translate-x-1/2 rounded-[10px] bg-[var(--text-primary)] px-4 py-2.5 text-[13px] font-semibold text-white shadow-[0_8px_24px_rgba(36,41,54,.28)]">
          {toast}
        </div>
      ) : null}
    </div>
  );
}

function Row({
  row,
  last,
  onOpen,
  onToggle,
  onReport,
  onEdit,
  onContinue,
  onDuplicate,
  onDelete,
}: {
  row: RcListRow;
  last: boolean;
  onOpen: () => void;
  onToggle: () => void;
  onReport: () => void;
  onEdit: () => void;
  onContinue: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const draft = row.state === 'draft';
  return (
    <div
      onClick={onOpen}
      className={cn('grid cursor-pointer items-center gap-x-3 px-4 py-3.5 hover:bg-[var(--surface-sunken)]', COLS)}
      style={last ? undefined : { borderBottom: '1px solid var(--border-default)' }}
    >
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-semibold text-[var(--text-primary)]">{row.name}</span>
        <span className="block text-[11.5px] text-[var(--text-muted)]">
          {LIST_GOAL_LABEL[row.goal]} · {draft ? 'draft' : `since ${row.since ?? '—'}`}
        </span>
      </span>

      <span onClick={(e) => { e.stopPropagation(); }}>
        {draft ? (
          // A draft has no toggle — there is nothing to pause.
          <span className="text-[12px] font-semibold text-[var(--text-muted)]">Draft</span>
        ) : (
          <span className="inline-flex flex-col gap-0.5">
            <button
              type="button"
              role="switch"
              aria-checked={row.state === 'active'}
              aria-label={`${row.name} is ${row.state}`}
              onClick={onToggle}
              className="inline-flex items-center gap-1.5"
            >
              <span
                className={cn(
                  'relative h-[18px] w-[30px] rounded-full transition',
                  row.state === 'active' ? 'bg-[var(--money-green)]' : 'bg-[var(--border-strong)]',
                )}
              >
                <span
                  className={cn(
                    'absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-all',
                    row.state === 'active' ? 'left-[14px]' : 'left-[2px]',
                  )}
                />
              </span>
              <span
                className={cn(
                  'text-[12px] font-semibold',
                  row.state === 'active' ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]',
                )}
              >
                {row.state === 'active' ? 'Active' : 'Paused'}
              </span>
            </button>
            {row.pausedOn !== undefined ? (
              <span className="text-[10.5px] text-[var(--text-muted)]">Paused {row.pausedOn}</span>
            ) : null}
          </span>
        )}
      </span>

      <span className="font-[var(--font-mono)] text-[13px] text-[var(--text-secondary)]">
        {row.inProgress === undefined ? '—' : row.inProgress.toLocaleString('en-US')}
      </span>

      {/* Icons, not the `T E C H` letters a practice had to learn. */}
      <span className="flex min-w-0 items-center gap-2">
        {row.steps.length === 0 ? (
          <span className="text-[12px] text-[var(--text-muted)]">No steps yet</span>
        ) : (
          <>
            <span className="shrink-0 text-[12px] text-[var(--text-secondary)]">{row.steps.length} steps</span>
            <span className="flex items-center gap-1">
              {row.steps.map((c, i) => (
                <span
                  key={`${c}-${String(i)}`}
                  title={CHANNEL_LABEL[c]}
                  className="flex h-[20px] w-[20px] items-center justify-center rounded-[6px] bg-[var(--chip-fill)]"
                >
                  <Icon kind={CHANNEL_ICON[c] as IconKind} size={11} stroke={2} className="text-[var(--text-muted)]" />
                </span>
              ))}
            </span>
          </>
        )}
      </span>

      {/* One number, named by the goal — no percentages anywhere. */}
      <span className="text-[12.5px] text-[var(--text-secondary)]">
        {row.last30 === undefined ? (
          '—'
        ) : (
          <>
            <b className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{row.last30}</b>{' '}
            {resultNoun(row.goal)}
          </>
        )}
      </span>

      <span
        onClick={(e) => { e.stopPropagation(); }}
        className="flex flex-wrap items-center justify-end gap-x-2.5 gap-y-1"
      >
        {draft ? (
          <Action onClick={onContinue} primary>
            Continue
          </Action>
        ) : (
          <>
            <Action onClick={onReport} primary>
              Report
            </Action>
            <Action onClick={onEdit}>Edit</Action>
          </>
        )}
        <Action onClick={onDuplicate}>Duplicate</Action>
        <Action onClick={onDelete} danger>
          Delete
        </Action>
      </span>
    </div>
  );
}

function Action({
  children,
  onClick,
  primary,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-[11.5px] font-semibold hover:underline',
        primary === true && 'text-[var(--brand-indigo)]',
        danger === true && 'text-[var(--alert-red)]',
        primary !== true && danger !== true && 'text-[var(--text-secondary)]',
      )}
    >
      {children}
    </button>
  );
}

/**
 * The delete confirmation (§5). A running recall says **what happens to the
 * patients in it**, and that the history stays — deleting a recall is not
 * deleting what it did.
 */
function DeleteModal({
  row,
  onCancel,
  onConfirm,
}: {
  row: RcListRow;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ViewportOverlay open onClose={onCancel}>
      <div
        onClick={(e) => { e.stopPropagation(); }}
        className="w-[min(480px,calc(100vw-32px))] overflow-hidden rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[0_18px_48px_rgba(36,41,54,.18)]"
      >
        <div className="px-6 py-5">
          <h2 className="m-0 text-[16px] font-bold text-[var(--text-primary)]">Delete {row.name}?</h2>
          <p className="m-0 mt-2 text-[12.5px] leading-[1.7] text-[var(--text-secondary)]">
            {row.state === 'draft' ? (
              'This draft hasn’t sent anything.'
            ) : (
              <>
                The {(row.inProgress ?? 0).toLocaleString('en-US')} patients in it won’t get any more of its messages.
                What it already sent and booked stays in the Communication log and on the Dashboard.
              </>
            )}
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-secondary)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-[10px] bg-[var(--alert-red)] px-4 py-2 text-[13.5px] font-semibold text-white"
          >
            Delete
          </button>
        </div>
      </div>
    </ViewportOverlay>
  );
}

/** Only reachable by deleting everything — a new practice starts with drafts. */
function EmptyState({ onNew }: { onNew: (() => void) | undefined }) {
  return (
    <div className="px-4 py-12 text-center">
      <div className="text-[14px] font-bold text-[var(--text-primary)]">No recalls yet</div>
      <div className="mt-1 text-[12.5px] text-[var(--text-muted)]">
        Build your first one — it takes a few minutes.
      </div>
      <button
        type="button"
        onClick={onNew}
        className="mt-3 rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2 text-[13.5px] font-semibold text-white"
      >
        New recall
      </button>
    </div>
  );
}
